import { Value } from 'baseui/select';
import { capitalize, generateId } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import { differenceInCalendarDays } from 'date-fns';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { addNewDocumentPurchaseGuest } from '../../hooks/use-get-default-values';
import {
  DocumentPurchaseItemGuestDetailFormType,
  DocumentPurchaseItemGuestFormType,
} from '../form-type';

function useGetInsuranceTableList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:type'),
      accessorKey: 'type',
      minSize: 100,
      dataType: ColumnDataType.select,
      required: true,
      options: [
        { id: 'duo', label: t('common:duo') },
        { id: 'family', label: t('common:family') },
        { id: 'group', label: t('common:group') },
        { id: 'individual', label: t('common:individual') },
      ],
    },
    {
      header: t('common:started_at'),
      accessorKey: 'startedAt',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:ended_at'),
      accessorKey: 'endedAt',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:days'),
      accessorKey: 'days',
      minSize: 80,
      maxSize: 80,
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
    },
    {
      header: t('common:package'),
      accessorKey: 'package',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:company'),
      accessorKey: 'company',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:cover_area'),
      accessorKey: 'coverArea',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 300,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    ...deleteColumn(editable),
  ];
}

const InsuranceTable = (props: { parentIndex: number }) => {
  const { editable } = useFormState();
  const { control, clearErrors, setValue, getValues, trigger } =
    useFormContext();

  const name =
    `documentPurchases.${props.parentIndex}.documentPurchaseGuests` as const;

  const fields = useWatch({
    control,
    name,
  });

  const { remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetInsuranceTableList();

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentActionRow = action.rowIndex as number;
      const guest = guests[currentActionRow];

      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof DocumentPurchaseItemGuestFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<DocumentPurchaseItemGuestFormType> = () => {
        if (columnId === 'guestId') {
          return {
            guestName: extra?.[0]
              ? `${capitalize(extra?.[0]?.extra?.title)} ${
                  extra?.[0].extra.name
                }`
              : '',
          };
        } else {
          const startedAt = columnId === 'startedAt' ? value : guest.startedAt;
          const endedAt = columnId === 'endedAt' ? value : guest.endedAt;
          const days = (() => {
            try {
              const value = differenceInCalendarDays(endedAt, startedAt) + 1;
              if (!value) return 0;
              return value;
            } catch {
              return 0;
            }
          })();

          const detail: DocumentPurchaseItemGuestDetailFormType = {
            type: guest.type,
            description: guest.description,
            price: guest.price,
            startedAt,
            endedAt,
            days,
            company: guest.company,
            coverArea: guest.coverArea,
            package: guest.package,
            [columnId]: value,
            appointmentAt: null,
            biometric: false,
            customId: generateId(),
            id: undefined,
            label: '',
            visaDocumentId: '',
          };
          return { details: [detail], days };
        }
      };

      if (type === 'addRow') {
        const addRow: DocumentPurchaseItemGuestFormType[] = [
          ...guests,
          addNewDocumentPurchaseGuest(),
        ];
        setValue(name, addRow);
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentActionRow);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentActionRow, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(name);
      }
    },
    [clearErrors, getValues, name, remove, setValue, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
};

export default InsuranceTable;
